import { useCallback, useEffect, useRef, useState } from "react";
import { useAppRuntime } from "../../../providers/AppRuntimeProvider";
import type { AllowlistEntry } from "../types";

interface UseAllowlistResult {
  readonly entries: readonly AllowlistEntry[];
  readonly loading: boolean;
  readonly getMatchingEntries: (
    action: string,
    targetType: string,
    targetId: string,
    episodeId: string | null | undefined,
  ) => readonly AllowlistEntry[];
  readonly getMatchingEntry: (
    action: string,
    targetType: string,
    targetId: string,
    episodeId: string | null | undefined,
  ) => AllowlistEntry | undefined;
  readonly removeMatchingEntries: (
    action: string,
    targetType: string,
    targetId: string,
    episodeId: string | null | undefined,
  ) => Promise<void>;
  readonly addEntry: (
    action: string,
    targetType: string,
    targetId: string,
    label?: string,
    episodeId?: string | null,
  ) => Promise<void>;
  readonly removeEntry: (id: string) => Promise<void>;
  readonly updateAccess: (id: string, accessLevel: string, groupIds: string[], hookIds: string[]) => Promise<void>;
  readonly getEntry: (id: string) => Promise<AllowlistEntry>;
  readonly reload: () => Promise<void>;
}

export function useAllowlist(): UseAllowlistResult {
  const runtime = useAppRuntime();
  const [entries, setEntries] = useState<AllowlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const result = await runtime.transport.rpc<AllowlistEntry[]>("allowlist.list");
      setEntries(result);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [load]);

  const getMatchingEntries = useCallback(
    (
      action: string,
      targetType: string,
      targetId: string,
      episodeId: string | null | undefined,
    ): readonly AllowlistEntry[] =>
      entries.filter(
        (entry) =>
          entry.action === action &&
          entry.target_type === targetType &&
          entry.target_id === targetId &&
          (entry.episode_id === null || entry.episode_id === episodeId),
      ),
    [entries],
  );

  // @tested-by: tst_fe_allowlist_match_001
  const getMatchingEntry = useCallback(
    (
      action: string,
      targetType: string,
      targetId: string,
      episodeId: string | null | undefined,
    ): AllowlistEntry | undefined => {
      const matching = getMatchingEntries(
        action,
        targetType,
        targetId,
        episodeId,
      );
      return (
        matching.find((entry) => entry.episode_id === null) ??
        (episodeId
          ? matching.find((entry) => entry.episode_id === episodeId)
          : undefined)
      );
    },
    [getMatchingEntries],
  );

  const addEntry = useCallback(
    async (
      action: string,
      targetType: string,
      targetId: string,
      label?: string,
      episodeId?: string | null,
    ): Promise<void> => {
      const persistedEpisodeId = episodeId ?? null;
      try {
        const entry = await runtime.transport.rpc<AllowlistEntry>("allowlist.add", {
          action,
          target_type: targetType,
          target_id: targetId,
          targetLabel: label ?? null,
          episode_id: persistedEpisodeId,
        });
        setEntries((prev) => {
          const filtered = prev.filter(
            (e) =>
              !(
                e.action === action &&
                e.target_type === targetType &&
                e.target_id === targetId &&
                e.episode_id === persistedEpisodeId
              ),
          );
          return [entry, ...filtered];
        });
      } catch (err) {
        console.error("Failed to add allowlist entry:", err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const removeEntry = useCallback(async (id: string): Promise<void> => {
    try {
      await runtime.transport.rpc("allowlist.delete", { id });
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      console.error("Failed to remove allowlist entry:", err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // @tested-by: tst_fe_allowlist_revoke_001
  const removeMatchingEntries = useCallback(
    async (
      action: string,
      targetType: string,
      targetId: string,
      episodeId: string | null | undefined,
    ): Promise<void> => {
      const matching = getMatchingEntries(
        action,
        targetType,
        targetId,
        episodeId,
      );
      await Promise.all(matching.map(async (entry) => removeEntry(entry.id)));
    },
    [getMatchingEntries, removeEntry],
  );

  const updateAccess = useCallback(
    async (id: string, accessLevel: string, groupIds: string[], hookIds: string[]): Promise<void> => {
      try {
        const updated = await runtime.transport.rpc<AllowlistEntry>("allowlist.update_access", {
          id,
          access_level: accessLevel,
          group_ids: groupIds,
          hookIds: hookIds,
        });
        setEntries((prev) => prev.map((e) => (e.id === id ? updated : e)));
      } catch (err) {
        console.error("Failed to update allowlist access:", err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getEntry = useCallback(async (id: string): Promise<AllowlistEntry> => {
    return runtime.transport.rpc<AllowlistEntry>("allowlist.get", { id });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    entries,
    loading,
    getMatchingEntries,
    getMatchingEntry,
    addEntry,
    removeEntry,
    removeMatchingEntries,
    updateAccess,
    getEntry,
    reload: load,
  };
}
